/**
 * Per-Nutzer Reihenfolge der primären Topbar-Tabs — wie das Bookmark-
 * Leisten-Muster im Browser (jede:r ordnet sich die wichtigsten Tabs
 * selbst an). Persistiert als JSON-Array von Route-IDs in
 * `profiles.nav_tab_order`. Bewusst pro Nutzer, nicht pro Organisation
 * — im Unterschied zu `organizations.nav_overrides` (Sichtbarkeit,
 * org-weit, siehe `src/features/visibility.ts`).
 *
 * Pure functions extrahiert, damit sie ohne React/Supabase testbar sind
 * (gleiches Muster wie `visibility.ts`/`permissions.ts`).
 */

import type { RouteModule } from './types';

/**
 * Parst den rohen `jsonb`-Wert aus `profiles.nav_tab_order` in ein
 * Array von Route-IDs. Gibt `null` zurück bei fehlendem/ungültigem Wert
 * — der Aufrufer fällt dann auf die Code-Standardreihenfolge zurück.
 */
export function parseNavTabOrder(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null;
  const out = raw.filter((v): v is string => typeof v === 'string');
  return out.length > 0 ? out : null;
}

/**
 * Wendet eine gespeicherte Reihenfolge auf eine Liste von Routen an.
 * Routen, die (noch) nicht in `order` vorkommen (neue Routen, weil der
 * Nutzer noch nie umsortiert hat), werden in ihrer ursprünglichen
 * Reihenfolge ans Ende angehängt. Ids in `order`, die nicht mehr in
 * `routes` vorkommen (entfernte/deaktivierte Routen), werden
 * stillschweigend ignoriert. Reine Funktion — mutiert `routes` nicht.
 */
export function applyNavTabOrder(
  routes: readonly RouteModule[],
  order: readonly string[] | null,
): RouteModule[] {
  if (!order || order.length === 0) return [...routes];
  const byId = new Map(routes.map((r) => [r.id, r] as const));
  const ordered: RouteModule[] = [];
  for (const id of order) {
    const r = byId.get(id);
    if (r) {
      ordered.push(r);
      byId.delete(id);
    }
  }
  for (const r of routes) {
    if (byId.has(r.id)) ordered.push(r);
  }
  return ordered;
}
